import { SIGNED_URL_EXPIRATION_MS } from "@/configs/storage";
import { isDefined } from "@/utils/format";

import type { BucketFile } from "@/utils/storage/types";

export const getFromLocalStorage = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.error("Error getting from localStorage:", error);
    return null;
  }
};
export const setToLocalStorage = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.error("Error setting to localStorage:", error);
  }
};
export const removeFromLocalStorage = (key: string) => {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.error("Error removing from localStorage:", error);
  }
};
const getFutureDateISO = (ms: number): string => new Date(Date.now() + ms).toISOString();

export const clearLocalStorage = () => {
  [window.localStorage, window.sessionStorage].forEach((storage) => {
    try {
      storage.clear();
    } catch (error) {
      console.error("Error clearing storage:", error);
    }
  });
};

interface CachedFile {
  id: string;
  bucket_id: string;
  path: string;
  url: string;
  expiration: string;
}

export const getStorageCacheKey = (file: BucketFile, identifier = "") => `STORAGE::FILE_CACHE${identifier}::${file.id}`;

interface SetCachedSignedFileUrlProps {
  file: BucketFile;
  identifier?: string;
  url?: string;
  expiration?: number;
}
export const setCachedSignedFileUrl = ({
  file,
  identifier = "",
  url = "",
  expiration = SIGNED_URL_EXPIRATION_MS,
}: SetCachedSignedFileUrlProps) => {
  const cacheKey = getStorageCacheKey(file, identifier);
  const cachedFile: CachedFile = {
    id: file.id,
    bucket_id: file.bucket_id,
    path: file.path,
    url: url,
    expiration: getFutureDateISO(expiration),
  };
  setToLocalStorage(cacheKey, JSON.stringify(cachedFile));
};

interface GetCachedSignedFileUrlProps {
  file: BucketFile;
  identifier?: string;
}
export const getCachedSignedFileUrl = ({ file, identifier = "" }: GetCachedSignedFileUrlProps) => {
  const cacheKey = getStorageCacheKey(file, identifier);
  const cachedFileString = getFromLocalStorage(cacheKey);

  if (!cachedFileString) {
    return undefined;
  }

  try {
    const cachedFile = JSON.parse(cachedFileString) as CachedFile;

    if (
      !isDefined(cachedFile.id) || // id can be empty string
      !cachedFile.bucket_id ||
      !cachedFile.path ||
      !cachedFile.expiration
    ) {
      throw new Error("Malformed cached file");
    }

    const idValid = cachedFile.id === file.id;
    const bucketValid = cachedFile.bucket_id === file.bucket_id;
    const pathValid = cachedFile.path === file.path;
    const expValid = new Date(cachedFile.expiration).getTime() > new Date().getTime();

    const isValid = idValid && bucketValid && pathValid && expValid;

    if (isValid) {
      return cachedFile.url || null;
    } else {
      removeFromLocalStorage(cacheKey);
      return undefined;
    }
  } catch (error) {
    console.error("Error parsing cached file:", error);
    removeFromLocalStorage(cacheKey);
    return undefined;
  }
};
